import { useTranslation } from 'react-i18next';
import type { PlatformActivity } from '@/api/platformMetrics/platformMetricsApi.types';
import { ChartCard } from '@/components/common/charts/ChartCard';
import { TrendAreaChart, type TrendAreaSeries } from '@/components/common/charts/TrendAreaChart';
import { usePlatformActivityQuery } from '@/hooks/usePlatformChartsQueries';
import { resolveSeriesColor } from '@/theme/chartPalette';
import { formatIsoDateToDayAndMonth } from '@/utils/dateFormatting';

interface ActivityTrendChartProps {
  isEnabled: boolean;
}

interface ActivityRow extends Record<string, string | number> {
  date: string;
  events: number;
}

export function ActivityTrendChart({ isEnabled }: ActivityTrendChartProps) {
  const { t } = useTranslation('admin');
  const activityQuery = usePlatformActivityQuery(isEnabled);
  const activity = activityQuery.data;
  const rows = activity === undefined ? [] : buildActivityRows(activity);
  const totalEvents = rows.reduce(function addEvents(sum, row) {
    return sum + row.events;
  }, 0);
  const windowDays = activity === undefined ? 0 : activity.days;

  const series: TrendAreaSeries[] = [
    {
      key: 'events',
      label: t('dashboard.charts.activity.eventsSeries'),
      color: resolveSeriesColor(0),
    },
  ];

  return (
    <ChartCard
      title={t('dashboard.charts.activity.title', { days: windowDays })}
      iconName="Activity"
      isLoading={activityQuery.isLoading === true}
      isError={activityQuery.isError === true}
      onRetry={function retry() {
        activityQuery.refetch();
      }}
      isEmpty={totalEvents === 0}
      emptyLabel={t('dashboard.charts.activity.empty')}
    >
      <TrendAreaChart
        data={rows}
        xKey="date"
        series={series}
        accessibleLabel={t('dashboard.charts.activity.title', { days: windowDays })}
        formatXTick={formatIsoDateToDayAndMonth}
        formatValue={formatWholeNumber}
      />
    </ChartCard>
  );
}

function buildActivityRows(activity: PlatformActivity): ActivityRow[] {
  return activity.daily.map(function toRow(day) {
    return { date: day.date, events: day.count };
  });
}

function formatWholeNumber(value: number): string {
  return String(value);
}
